// @givmo/compliance — the Calendar, the module's landing (CCM-R1-INTAKE (a); set C Q1; frames 01,
// 02, 07, 09; the reminders notice of rw-01 and rw-02).
//
// What needs the organization's input, what is coming up and what Givmo cannot date yet, each in its
// own section (`compliance-calendar-model.ts`), then row 7's sentence as one line at the foot while a
// library filing's worked-out date or basis shows. With nothing recorded, the first visit stands in.
//
// ══ ONE FILING, ONE PANEL, ONE ADDRESS ═════════════════════════════════════════════════════════
//
// A filing opens the same panel Filings opens, over the Calendar (DESIGN-NOTE-1b §4.1). The address
// bar shows the panel's own address (`/compliance/filings/<id>`, route masking), so a copied or
// bookmarked link is the filing's, and a reload opens it over Filings (`unmaskOnReload`). The
// Calendar keeps it open from its own search (`?filing=`), and the rule view the same way.
//
// ⚠ The page reads exactly what Filings reads, under the same keys (`compliance-filings-reads.ts`):
// one composition, so the Calendar and Filings can never disagree about a filing.

import { useState, type MouseEvent, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Button,
  Card,
  EmptyState,
  FOCUS_RING,
  Icon,
  PageHeader,
  Skeleton,
  cn,
  shouldInterceptClick,
  testIdProp,
} from '@givmo/design-system';
import { Building2 } from 'lucide-react';
import {
  composeCalendar,
  type CalendarDate,
  type CalendarGap,
  type CalendarQuestion,
} from './compliance-calendar-model';
import { useCompliance } from './compliance-context';
import { OPEN_THE_FILING } from './review-words';
import { serverMessage } from './compliance-decode';
import { ComplianceDisclosure } from './compliance-disclosure';
import { useFilingsComposition } from './compliance-filings-reads';
import type { FilingRef } from './compliance-filings-model';
import { OVERDUE } from './compliance-panel-model';
import { COMPLIANCE_PAGE_TEST_IDS, COMPLIANCE_ROUTES } from './compliance-routes';
import { ModuleUnavailableNotice, sourceChangeShortForm } from './compliance-shared';
import { AddFilingButton, AddFilingSheet } from './add-filing-sheet';
import { UploadDocumentsButton } from './documents-page';
import { UploadDocumentsSheet } from './upload-sheet';
import { FilingPanel } from './filing-panel';
import { FirstVisit } from './first-visit';
import {
  linkedCalendarRows,
  vendorCalendarDates,
  type VendorCalendarDate,
} from './compliance-vendor-model';
import type { ObligationVendorLink } from './compliance-decode';
import {
  AddVendorRecordSheet,
  LinkedRecordLine,
  RECORD_SEARCH_KEY,
  RecordsNamingLine,
  VendorCalendarRow,
  useVendorRecords,
} from './vendor-records';
import { RemindersNotice } from './reminders-notice';
import { ReviewEntryCard } from './review-entry';

const PREFIX = 'compliance-calendar';

function SectionTitle({ children }: { children: ReactNode }) {
  return <h2 className="text-s15 font-w600 text-text-strong">{children}</h2>;
}

function ListCard({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <Card className="overflow-hidden" testId={testId}>
      <ul className="divide-y divide-border-subtle">{children}</ul>
    </Card>
  );
}

/** A filing's name: its panel's address, followed in place (the row around it opens it too). */
function FilingName({
  title,
  filingRef,
  onOpen,
  testId,
}: {
  title: string;
  filingRef: FilingRef;
  onOpen: () => void;
  testId: string;
}) {
  return (
    <a
      href={COMPLIANCE_ROUTES.filing(filingRef.id)}
      onClick={(event) => {
        event.stopPropagation();
        if (!shouldInterceptClick(event)) return;
        event.preventDefault();
        onOpen();
      }}
      className={cn(
        'w-fit rounded-xs text-s14 font-w600 text-text-strong hover:underline hover:underline-offset-2',
        FOCUS_RING,
      )}
      {...testIdProp(testId)}
    >
      {title}
    </a>
  );
}

/** The row opens its filing on a click anywhere a link or button does not answer it. */
function rowClick(onOpen: () => void) {
  return (event: MouseEvent<HTMLLIElement>) => {
    if ((event.target as HTMLElement).closest('a, button') !== null) return;
    onOpen();
  };
}

function QuestionRow({
  question,
  canAnswer,
  onOpen,
}: {
  question: CalendarQuestion;
  canAnswer: boolean;
  onOpen: () => void;
}) {
  return (
    <li
      onClick={rowClick(onOpen)}
      className="flex cursor-pointer flex-col gap-12 px-20 py-16 hover:bg-surface-hover sm:flex-row sm:items-center sm:justify-between sm:gap-24"
      {...testIdProp(`${PREFIX}-question-${question.key}`)}
    >
      <div className="flex min-w-0 flex-col gap-4">
        <FilingName
          title={question.title}
          filingRef={question.ref}
          onOpen={onOpen}
          testId={`${PREFIX}-question-name-${question.key}`}
        />
        <p className="max-w-[640px] text-s13 leading-h15 text-text-secondary">
          {question.question}
        </p>
      </div>
      {/* A viewer reads the question in the panel; only a writer is offered the answer. */}
      {canAnswer && (
        <div className="shrink-0">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onOpen}
            testId={`${PREFIX}-question-add-${question.key}`}
          >
            {question.opens === true ? OPEN_THE_FILING : 'Add the date'}
          </Button>
        </div>
      )}
    </li>
  );
}

function DateRow({ date, onOpen }: { date: CalendarDate; onOpen: () => void }) {
  // Q3's short form (CCM-OPS1-WEB; ruling 6, frame a3): after the basis it qualifies, lighter; on a
  // row whose basis has no approved words, alone where the basis would be.
  const change = sourceChangeShortForm(date.sourceChangeFoundOn);
  return (
    <li
      onClick={rowClick(onOpen)}
      className="grid cursor-pointer grid-cols-1 items-start gap-y-4 px-20 py-16 hover:bg-surface-hover sm:grid-cols-[112px_minmax(0,1fr)] sm:gap-x-20 sm:gap-y-0"
      {...testIdProp(`${PREFIX}-date-${date.key}`)}
    >
      {/* On a phone the date stands above the filing; a row with no date shows no empty line. */}
      <div className={cn('flex flex-col gap-2', date.dateLabel === '' && 'hidden sm:flex')}>
        <p
          className="text-s14 font-w600 tabular-nums text-text-strong"
          {...testIdProp(`${PREFIX}-date-when-${date.key}`)}
        >
          {date.dateLabel}
        </p>
        {/* MDEXT-OPEN: N1, while the year is held open by its automatic extension. */}
        {date.datePassed !== undefined && (
          <p
            className="text-s12 text-text-secondary"
            {...testIdProp(`${PREFIX}-date-passed-${date.key}`)}
          >
            {date.datePassed}
          </p>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-4">
        <FilingName
          title={date.title}
          filingRef={date.ref}
          onOpen={onOpen}
          testId={`${PREFIX}-date-name-${date.key}`}
        />
        {date.basis !== undefined ? (
          <p className="text-s13 text-text-secondary">
            {date.basis}
            {change !== undefined && (
              <span
                className="text-text-tertiary"
                {...testIdProp(`${PREFIX}-date-source-change-${date.key}`)}
              >
                {' · '}
                {change}
              </span>
            )}
          </p>
        ) : (
          change !== undefined && (
            <p
              className="text-s13 text-text-tertiary"
              {...testIdProp(`${PREFIX}-date-source-change-${date.key}`)}
            >
              {change}
            </p>
          )
        )}
        {/* CCM-R1-RESULTS-WEB: what came back, where it's an exception, at body weight. */}
        {date.exception !== undefined && (
          <p className="text-s13 text-text" {...testIdProp(`${PREFIX}-date-exception-${date.key}`)}>
            {date.exception}
          </p>
        )}
        {date.lines.map((line) => (
          <p key={line} className="text-s12 text-text-tertiary">
            {line}
          </p>
        ))}
      </div>
    </li>
  );
}

/**
 * The Calendar's own dates and the organization's recorded renewal dates (J2), in one list by date:
 * a date before a later one, a filing with no date after every dated row, ties by name.
 */
function comingUp(
  dates: readonly CalendarDate[],
  records: readonly VendorCalendarDate[],
): (CalendarDate | VendorCalendarDate)[] {
  const dueOn = (entry: CalendarDate | VendorCalendarDate) => entry.dueOn;
  return [...dates, ...records].sort((a, b) => {
    const left = dueOn(a);
    const right = dueOn(b);
    if (left !== undefined && right !== undefined && left !== right) {
      return left.localeCompare(right);
    }
    if (left === undefined && right !== undefined) return 1;
    if (left !== undefined && right === undefined) return -1;
    return a.title.localeCompare(b.title);
  });
}

function GapRow({
  gap,
  link,
  onOpen,
  onOpenRecord,
}: {
  gap: CalendarGap;
  /** The record this filing is linked to, whose date it can't show (undated, or ended). */
  link?: ObligationVendorLink;
  onOpen: () => void;
  onOpenRecord: (vendorId: string) => void;
}) {
  return (
    <li
      onClick={rowClick(onOpen)}
      className="flex cursor-pointer flex-col gap-4 px-20 py-16 hover:bg-surface-hover"
      {...testIdProp(`${PREFIX}-gap-${gap.key}`)}
    >
      <FilingName
        title={gap.title}
        filingRef={gap.ref}
        onOpen={onOpen}
        testId={`${PREFIX}-gap-name-${gap.key}`}
      />
      <p className="text-s13 leading-h15 text-text-secondary">{gap.reason}</p>
      {gap.last !== undefined && (
        <p
          className={
            gap.lastException === true ? 'text-s13 text-text' : 'text-s12 text-text-tertiary'
          }
        >
          {gap.last}
        </p>
      )}
      {/* CCM-MOD5-RECORD-FILING-LINK (the design stop's 4.4 (b)): a linked filing with no date to
          show says, quietly, what its panel says in V8b's place. */}
      {link !== undefined && (
        <LinkedRecordLine obligationId={gap.ref.id} link={link} quiet onOpenRecord={onOpenRecord} />
      )}
      {/* CCM-MOD5B (V6): the organization's records of this filing's kind, named, never merged. */}
      {gap.ref.kind === 'library' && (
        <RecordsNamingLine obligationId={gap.ref.id} quiet onOpenRecord={onOpenRecord} />
      )}
    </li>
  );
}

export function CalendarPage() {
  const {
    orgRef,
    isReady,
    isPending: orgPending,
    isError: orgError,
    error,
    operations,
    moduleStanding,
    canWrite,
    personName,
  } = useCompliance();
  // Who recorded an answer that came back after a send (CCM-R1-RESULTS-WEB).
  const nameOf = (userId: string | undefined) =>
    userId !== undefined ? personName?.(userId) : undefined;
  // The host's D-CPO-24 answer: nothing is read without an affirmative `'available'`.
  const available = moduleStanding === 'available';
  const live = isReady && available;
  const composition = useFilingsComposition(operations, live ? orgRef : null);
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { filing?: unknown; view?: unknown };
  const panelFilingId = typeof search.filing === 'string' ? search.filing : undefined;
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [addingRecord, setAddingRecord] = useState(false);
  const { failed, moduleDark, loading } = composition;
  // Nothing is shown from the reads unless standing is affirmed and every read answered.
  const view =
    live &&
    !moduleDark &&
    failed === undefined &&
    composition.records !== undefined &&
    composition.rows !== undefined
      ? composeCalendar(composition.records, composition.rows, composition.followThrough, nameOf)
      : undefined;

  // The panel over the Calendar, at its own address (route masking).
  const openPanel = (filingId: string, panel: 'filing' | 'rule' = 'filing') =>
    void navigate({
      to: COMPLIANCE_ROUTES.calendar,
      search: panel === 'rule' ? { filing: filingId, view: 'rule' } : { filing: filingId },
      mask: {
        to:
          panel === 'rule'
            ? COMPLIANCE_ROUTES.filingRule(filingId)
            : COMPLIANCE_ROUTES.filing(filingId),
        unmaskOnReload: true,
      },
    });

  const panel = panelFilingId !== undefined && live && (
    <FilingPanel
      filingId={panelFilingId}
      view={search.view === 'rule' ? 'rule' : 'filing'}
      onClose={() => void navigate({ to: COMPLIANCE_ROUTES.calendar })}
      onNavigate={(to, search) => {
        // The panel's own addresses stay over the Calendar; anything else is followed as it is.
        if (to === COMPLIANCE_ROUTES.filing(panelFilingId)) openPanel(panelFilingId);
        else if (to === COMPLIANCE_ROUTES.filingRule(panelFilingId))
          openPanel(panelFilingId, 'rule');
        else void navigate({ to, ...(search !== undefined ? { search } : {}) });
      }}
    />
  );

  // The add sheet, for a writer; the filing it adds opens in its panel over the Calendar.
  const sheet = canWrite === true && (
    <AddFilingSheet
      open={adding}
      onClose={() => setAdding(false)}
      onAdded={(filingId) => {
        setAdding(false);
        openPanel(filingId);
      }}
    />
  );
  const openSheet = canWrite === true ? () => setAdding(true) : undefined;
  const openUpload = canWrite === true ? () => setUploading(true) : undefined;
  const uploadSheet = canWrite === true && (
    <UploadDocumentsSheet open={uploading} onClose={() => setUploading(false)} />
  );

  // J2: each current record's renewal date, among the Calendar's own dates. Opening one opens its
  // panel on Filings (V6).
  const vendorRecords = useVendorRecords();
  const allRecordDates = vendorCalendarDates(vendorRecords.data ?? []);
  // L5 (D-CCM-97 (4)): the date shows once. A linked filing the record's date fills leaves "Not
  // supported yet" and takes the record's row, in V6's form, titled by the filing.
  const linkOf = (obligationId: string) =>
    composition.records?.obligations.find((obligation) => obligation.obligationId === obligationId)
      ?.vendorLink;
  const linked = linkedCalendarRows(view?.gaps ?? [], allRecordDates, linkOf, (gap) => gap.title);
  const recordDates = linked.recordDates;
  const openRecord = (vendorId: string) =>
    void navigate({ to: COMPLIANCE_ROUTES.filings, search: { [RECORD_SEARCH_KEY]: vendorId } });
  // D2 (D-CCM-97): the first visit's door opens V3's sheet here too; the record it adds opens in its
  // panel on Filings, where the records live.
  const recordSheet = canWrite === true && (
    <AddVendorRecordSheet
      open={addingRecord}
      onClose={() => setAddingRecord(false)}
      onAdded={(vendorId) => {
        setAddingRecord(false);
        openRecord(vendorId);
      }}
      onOpenRecord={(vendorId) => {
        setAddingRecord(false);
        openRecord(vendorId);
      }}
    />
  );
  // A recorded date is something to list (R8): the first visit only where there is none. It waits on
  // the records' read, and a failed read counts as none, so it never holds the first visit back.
  const recordsKnown = vendorRecords.data !== undefined || vendorRecords.isError;
  const waitingOnRecords = view?.firstVisit === true && !recordsKnown;

  // The notice is about the module's landing, whatever it shows (rw-01: "Calendar only").
  const firstVisit = view?.firstVisit === true && recordsKnown && recordDates.length === 0;
  const page = firstVisit ? (
    <FirstVisit
      pageTestId={COMPLIANCE_PAGE_TEST_IDS.calendar}
      notice={<RemindersNotice />}
      review={<ReviewEntryCard />}
      {...(openSheet !== undefined ? { onAddFiling: openSheet } : {})}
      {...(openUpload !== undefined ? { onUpload: openUpload } : {})}
      {...(canWrite === true ? { onAddRecord: () => setAddingRecord(true) } : {})}
    />
  ) : (
    <div
      className="flex w-full max-w-[1120px] flex-col gap-28 px-16 py-28 sm:px-24 lg:px-32"
      {...testIdProp(COMPLIANCE_PAGE_TEST_IDS.calendar)}
    >
      <PageHeader
        title="Calendar"
        {...(openSheet !== undefined && openUpload !== undefined
          ? {
              actions: (
                <>
                  <UploadDocumentsButton onClick={openUpload} />
                  <AddFilingButton onClick={openSheet} />
                </>
              ),
            }
          : {})}
      />

      {/* D-CCM-18: when nobody gets deadline reminders, the Calendar says so under its header. It
          reads its own answer, so it never waits on the Calendar's lists. */}
      <RemindersNotice />

      {/* The review's way in, while it holds something open (CCM-R1-REVIEW-WEB; frame 91). */}
      <ReviewEntryCard />

      {orgError && (
        <EmptyState
          testId={`${PREFIX}-org-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your organization"
          description={serverMessage(error, 'Please try again.')}
        />
      )}

      {!orgError && !isReady && orgPending && <Skeleton className="h-[320px] w-full" />}

      {isReady && available && moduleDark && (
        <ModuleUnavailableNotice testId={`${PREFIX}-unavailable`} />
      )}

      {isReady && available && !moduleDark && failed !== undefined && (
        <EmptyState
          testId={`${PREFIX}-error`}
          icon={<Icon icon={Building2} size={20} aria-hidden />}
          title="Couldn’t load your calendar"
          description={serverMessage(failed, 'Please try again.')}
        />
      )}

      {isReady &&
        available &&
        !moduleDark &&
        failed === undefined &&
        (loading || waitingOnRecords) && (
          <Skeleton className="h-[320px] w-full" {...testIdProp(`${PREFIX}-loading`)} />
        )}

      {view !== undefined && !waitingOnRecords && (
        <>
          {/* CCM-R1-LATE-FILINGS (D-CCM-62, frame 1d): years read overdue, a group at the top. */}
          {view.overdue.length > 0 && (
            <section className="flex flex-col gap-12">
              <SectionTitle>{OVERDUE}</SectionTitle>
              <ListCard testId={`${PREFIX}-overdue`}>
                {view.overdue.map((date) => (
                  <DateRow key={date.key} date={date} onOpen={() => openPanel(date.ref.id)} />
                ))}
              </ListCard>
            </section>
          )}
          {view.questions.length > 0 && (
            <section className="flex flex-col gap-12">
              <SectionTitle>Needs your input</SectionTitle>
              <ListCard testId={`${PREFIX}-questions`}>
                {view.questions.map((question) => (
                  <QuestionRow
                    key={question.key}
                    question={question}
                    canAnswer={canWrite === true}
                    onOpen={() => openPanel(question.ref.id)}
                  />
                ))}
              </ListCard>
            </section>
          )}
          {(view.dates.length > 0 || recordDates.length > 0) && (
            <section className="flex flex-col gap-12">
              <SectionTitle>Coming up</SectionTitle>
              <ListCard testId={`${PREFIX}-dates`}>
                {comingUp(view.dates, recordDates).map((entry) =>
                  'record' in entry ? (
                    <VendorCalendarRow
                      key={entry.key}
                      date={entry}
                      onOpen={() =>
                        entry.filingId !== undefined
                          ? openPanel(entry.filingId)
                          : openRecord(entry.record.vendorId)
                      }
                    />
                  ) : (
                    <DateRow key={entry.key} date={entry} onOpen={() => openPanel(entry.ref.id)} />
                  ),
                )}
              </ListCard>
            </section>
          )}
          {linked.gaps.length > 0 && (
            <section className="flex flex-col gap-12">
              <SectionTitle>Not supported yet</SectionTitle>
              <ListCard testId={`${PREFIX}-gaps`}>
                {linked.gaps.map((gap) => {
                  const link = gap.ref.kind === 'library' ? linkOf(gap.ref.id) : undefined;
                  return (
                    <GapRow
                      key={gap.key}
                      gap={gap}
                      {...(link !== undefined ? { link } : {})}
                      onOpen={() => openPanel(gap.ref.id)}
                      onOpenRecord={openRecord}
                    />
                  );
                })}
              </ListCard>
            </section>
          )}
          {/* Plan §3 row 7 (A-X1): one line at the foot, only while a library filing's worked-out
              date or basis shows. Registry-served; renders nothing until the row is served. */}
          {view.sentence && <ComplianceDisclosure surface="calendar" />}
        </>
      )}
    </div>
  );

  // The panel and both sheets keep ONE place in the tree, beside the page rather than inside it, so
  // the first visit replacing the loading page never remounts a sheet opened meanwhile, nor loses
  // what was typed in it (CCM-R1-INTAKE-SHEET-REMOUNT; the Filings page does the same).
  return (
    <>
      {page}
      {panel}
      {sheet}
      {uploadSheet}
      {recordSheet}
    </>
  );
}
